import { create } from "zustand";
import { persist } from "zustand/middleware";
import $api from "@/zapi-frontend/shared/api/axios";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { LS_ORGANIZATION_STORAGE } from "../shared/local-storage/keys";

export type Organization = {
  id: number;
  name: string;
};

type OrganizationState = {
  currentOrganizationId: number;
  availableOrganizations: Organization[];
  isLoading: boolean;
  hasLoadedOrganizations: boolean;
  isSystemAdmin: boolean;

  setCurrentOrganizationId: (id: number) => void;
  switchOrganization: (id: number) => void;
  loadOrganizations: () => Promise<void>;
  setAvailableOrganizations: (organizations: Organization[]) => void;
  updateOrganizationName: (id: number, name: string) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
  setIsSystemAdmin: (isSystemAdmin: boolean) => void;

  // Computed properties
  shouldShowPicker: () => boolean;
};

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      currentOrganizationId: 0,
      availableOrganizations: [],
      isLoading: false,
      hasLoadedOrganizations: false,
      isSystemAdmin: false,

      setCurrentOrganizationId: (id: number) => {
        set({ currentOrganizationId: id });
      },

      switchOrganization: (id: number) => {
        const { isSystemAdmin } = get();
        if (isSystemAdmin) {
          set({ currentOrganizationId: id });
        }
      },

      loadOrganizations: async () => {
        const { isSystemAdmin, hasLoadedOrganizations, isLoading } = get();

        if (!isSystemAdmin || hasLoadedOrganizations || isLoading) return;

        set({ isLoading: true });

        try {
          const response = await $api.get<{ data: Organization[]; message: string }>("/organizations/system-admin");
          set({
            availableOrganizations: response.data.data,
            hasLoadedOrganizations: true
          });
        } catch (error) {
          console.error("Failed to fetch organizations:", error);
          set({ availableOrganizations: [] });
        } finally {
          set({ isLoading: false });
        }
      },

      setAvailableOrganizations: (organizations: Organization[]) => {
        set({ availableOrganizations: organizations });
      },

      updateOrganizationName: (id: number, name: string) => {
        const { availableOrganizations } = get();
        set({
          availableOrganizations: availableOrganizations.map((org) => (org.id === id ? { ...org, name } : org))
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setIsSystemAdmin: (isSystemAdmin: boolean) => {
        set({ isSystemAdmin });
      },

      reset: () => {
        set({
          currentOrganizationId: 0,
          availableOrganizations: [],
          isLoading: false,
          hasLoadedOrganizations: false,
          isSystemAdmin: false
        });
      },

      shouldShowPicker: () => {
        const { isSystemAdmin, availableOrganizations } = get();
        return isSystemAdmin && availableOrganizations.length > 0;
      }
    }),
    {
      name: LS_ORGANIZATION_STORAGE,
      partialize: (state) => ({
        currentOrganizationId: state.currentOrganizationId
      }),
      storage: {
        getItem: (name) => {
          const item = localStorage.getItem(name);
          return item ? JSON.parse(item) : null;
        },
        setItem: (name, value) => {
          const meStore = useMeStore.getState();
          if (isSystemAdmin(meStore.role)) {
            localStorage.setItem(name, JSON.stringify(value));
          }
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        }
      }
    }
  )
);

export const initializeOrganizationStore = () => {
  const meStore = useMeStore.getState();
  const orgStore = useOrganizationStore.getState();

  const isAdmin = isSystemAdmin(meStore.role);

  orgStore.setIsSystemAdmin(isAdmin);
};

useMeStore.subscribe((state) => {
  const orgStore = useOrganizationStore.getState();

  const currentIsSystemAdmin = isSystemAdmin(state.role);
  if (currentIsSystemAdmin !== orgStore.isSystemAdmin) {
    orgStore.setIsSystemAdmin(currentIsSystemAdmin);
  }

  if (!currentIsSystemAdmin && orgStore.currentOrganizationId !== state.organizationId) {
    orgStore.setCurrentOrganizationId(state.organizationId || 0);
  } else if (state.organizationId && orgStore.currentOrganizationId === 0) {
    orgStore.setCurrentOrganizationId(state.organizationId);
  }
});
